import { useMemo } from "react";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDBadge from "components/MDBadge";
import { formatLocalDate, formatLocalTime } from "utils/dateFormatter";
import GenericListScreen from "components/GenericListScreen/GenericListScreen";
import { Column } from "components/GenericListScreen/types";

// Define the shape of the API Response item
interface StepRecordItem {
    id: number;
    recordDate: string;
    lastSyncDt: string;
    userName: string;
    stepCount: string;
    distanceKm: string;
    calorie: string;
    activeTimeMin: string;
    goalAchieved: string;
    syncSource: string;
    status: string;
    ipAddress: string;
    updateCount: number;
    rawStepCount: number;
}

const StepRecordListScreen = () => {

    // Define table columns
    const columns = useMemo<Column<StepRecordItem>[]>(
        () => [
            {
                Header: "마지막 기록",
                accessor: "recordDate",
                align: "center",
                width: "12%",
                Cell: ({ row }: any) => (
                    <MDBox lineHeight={1}>
                        <MDTypography display="block" variant="caption" fontWeight="medium">
                            {formatLocalDate(row.original.lastSyncDt)}
                        </MDTypography>
                        <MDTypography variant="caption" color="text" fontWeight="regular">
                            {formatLocalTime(row.original.lastSyncDt)}
                        </MDTypography>
                    </MDBox>
                )
            },
            {
                Header: "걸음수 날짜",
                accessor: "formattedRecordDate",
                align: "center",
                width: "12%",
                Cell: ({ row }: any) => (
                    <MDTypography variant="caption" fontWeight="medium">
                        {formatLocalDate(row.original.recordDate)}
                    </MDTypography>
                )
            },
            { Header: "사용자", accessor: "userName", align: "left", width: "15%" },
            {
                Header: "걸음수",
                accessor: "stepCount",
                align: "right",
                width: "10%",
                Cell: ({ row }: any) => (
                    <MDTypography variant="h5" fontWeight="bold" color="info">
                        {row.original.stepCount}
                    </MDTypography>
                )
            },
            { Header: "거리(km)", accessor: "distanceKm", align: "right", width: "10%" },
            { Header: "칼로리", accessor: "calorie", align: "right", width: "8%" },
            { Header: "활동시간(분)", accessor: "activeTimeMin", align: "right", width: "10%" },
            { Header: "목표달성", accessor: "goalAchieved", align: "center", width: "10%" },
            {
                Header: "상태",
                accessor: "status",
                align: "center",
                width: "8%",
                Cell: ({ row }: any) => {
                    const status = row.original.status;
                    if (status === "-") return <MDTypography variant="caption">-</MDTypography>;

                    const badgeColor = status === "OPEN" ? "success" : "secondary";
                    const badgeText = status === "OPEN" ? "OPEN" : "CLOSE";

                    return (
                        <MDBadge
                            badgeContent={badgeText}
                            color={badgeColor}
                            variant="gradient"
                            size="sm"
                        />
                    );
                }
            },
            { Header: "동기화소스", accessor: "syncSource", align: "center", width: "10%" },
            { Header: "IP 주소", accessor: "ipAddress", align: "center", width: "12%" },
            {
                Header: "업데이트횟수",
                accessor: "updateCount",
                align: "center",
                width: "10%",
                Cell: ({ row }: any) => (
                    <MDTypography variant="caption" color="text">
                        {row.original.updateCount}
                    </MDTypography>
                )
            },
        ],
        []
    );

    // Transform API data to row format
    const dataTransform = (data: any): { items: StepRecordItem[], totalCount: number } => {
        const items = (data.items || []).map((record: any) => {
            // 거리를 km로 변환
            const distanceKm = record.distanceMeter
                ? (record.distanceMeter / 1000).toFixed(2)
                : "-";

            // 목표 달성 표시
            const goalAchieved = record.isGoalAchieved === true
                ? "✓ 달성"
                : record.isGoalAchieved === false
                    ? "미달성"
                    : "-";

            return {
                id: record.seqId,
                recordDate: record.recordDate || null,
                lastSyncDt: record.lastSyncDt || null,
                userName: record.userName || "-",
                stepCount: record.stepCount?.toLocaleString() || "0",
                distanceKm,
                calorie: record.calorie || "-",
                activeTimeMin: record.activeTimeMin || "-",
                goalAchieved,
                syncSource: record.syncSource || "-",
                status: record.status || "-",
                ipAddress: record.ipAddress || "-",
                updateCount: record.updateCount ?? 0,
                rawStepCount: record.stepCount ?? 0,
            };
        });

        return {
            items,
            totalCount: data.totalCount || 0
        };
    };

    return (
        <GenericListScreen<StepRecordItem>
            title="걸음수 기록 목록 ( STEP RECORDS )"
            icon="directions_walk"
            apiName="StepRecord.pagedList"
            columns={columns}
            dataTransform={dataTransform}
        />
    );
};

export default StepRecordListScreen;
